import { inject, Injectable } from "@angular/core";
import { catchError, finalize, firstValueFrom, from, map, Observable, of, shareReplay, Subject, tap } from "rxjs";
import { AddOrRemoveRequest, AppLinks, ChildMenu, FavouriteLink, ParentMenu } from "../header-model";
import { BaseService } from "../../common/services/base-service";
import { environment } from "../../../environments/environment";
import {
  EasyAccessLink,
} from "../../easy-access/easy-access.model";
import { HeaderService } from "../header.service";
import { API_PATHS } from "../../common/constants/api-paths";
import { ApiResponse } from "../../common/models/api-response.model";
import { AuthType, CATALOG_AUTH_CONFIG, CatalogAuthConfig } from "../../core/config/catalog-auth.config";
import { GigyaService2 } from "../../common/services/gigya-service2";
import { Router } from "@angular/router";
import { ReusableService } from "../../common/services/reusable-services";
import { CacheService } from "../../common/services/cache-service";
import { CACHE_KEYS } from "../../common/constants/cache-keys";
import { ElementsService } from "../../common/services/elements-service";
import { UserDetailsPartial } from "../user-profile/user-profile.model";
import { mockEasyAccessResponse } from "../../common/utils/api-mocks";
import { SRP_ROUTES } from "../../common/constants/routes";
import { CatalogsViewService } from "../../landing-page/catalogs-view/catalogs-view.service";

@Injectable({
  providedIn: "root",
})
export class MenuService {
  public favsChanged = new Subject<ChildMenu>();
  favsChanged$ = this.favsChanged.asObservable();
  private readonly favoriteChanges = new Map<string, ChildMenu | null>();
  private easyAccessFavouritesInFlight$: Observable<ApiResponse<EasyAccessLink[]>> | null = null;
  baseService = inject(BaseService);
  headerService = inject(HeaderService);
  gigyaService2 = inject(GigyaService2);
  reusableService = inject(ReusableService);
  cacheService = inject(CacheService);
  elementService = inject(ElementsService);
  router = inject(Router);
  userDetails: UserDetailsPartial;
  catalogService = inject(CatalogsViewService);


  constructor() {
    this.userDetails = this.cacheService.getFromCache(CACHE_KEYS.USER.USER_DETAILS);

  }

  async fetchMenuWithFavorites(appLinks: AppLinks): Promise<{
    parentMenuLinks: ParentMenu[];
    favoriteCMs: ChildMenu[];
  }> {

    let easyAccessFavorites: EasyAccessLink[] = [];
    try {
      const favoritesEasyAccessLinks = await firstValueFrom(
        this.getEasyAccessFavourites()
      );
      easyAccessFavorites = favoritesEasyAccessLinks?.data || [];
    } catch (error: any) {
    }

    let parentMenuLinks: ParentMenu[] = this.normalizeParentMenu(appLinks.parentMenu || []);
    const favoriteCMs: ChildMenu[] = [];

    let aemFavouritesLinks: FavouriteLink[] = appLinks.favourites || [];
    let catalogInApplicability = this.catalogService.getCatalogInapplicability();

    // First, add items from favouritesLinks that are not in easyAccessFavorites
    //After this forEach we shall have AEM Favorites in favoriteCMs
    aemFavouritesLinks?.forEach((link) => {
      if (!easyAccessFavorites?.some(fav => fav.name === link.name)) {
        favoriteCMs.push({
          ...link,
          isFavorite: true,
          disabled: link.catalogId && catalogInApplicability[link.catalogId],
          visible: link.catalogId ? this.catalogService.getVisibleAndDisabledStatusOfCatalog(link.catalogId, catalogInApplicability) !== 'hide' : true
        } as ChildMenu);
      }
    });

    // Mark items in parentMenuLinks as favorite if they exist in easyAccessFavorites.
    // Traverse nested child menus as well so a subchild remains yellow when the menu is reopened.
    if (easyAccessFavorites?.length) {
      parentMenuLinks.forEach((pml) => {
        pml.childMenu.forEach((cm) => this.applyEasyAccessFavorite(cm, easyAccessFavorites, catalogInApplicability));
      });
    }

    // Add all easyAccessFavorites to favoriteCMs (avoid duplicates by checking if name already exists)
    easyAccessFavorites.forEach((link) => {
      if (!favoriteCMs.some(fav => fav.name === link.name)) {
        favoriteCMs.push({
          ...link,
          isFavorite: true,
          disabled: link.catalogId && catalogInApplicability[link.catalogId],
          visible: link.catalogId ? this.catalogService.getVisibleAndDisabledStatusOfCatalog(link.catalogId, catalogInApplicability) !== 'hide' : true
        } as ChildMenu);
      }
    });

    this.favoriteChanges.forEach((link, name) => {
      parentMenuLinks.forEach(parent => {
        parent.childMenu.forEach(child => this.applyFavoriteChange(child, name, !!link));
      });

      const favoriteIndex = favoriteCMs.findIndex(favorite => favorite.name === name);
      if (link && favoriteIndex === -1) {
        favoriteCMs.push({ ...link, isFavorite: true });
      } else if (!link && favoriteIndex !== -1) {
        favoriteCMs.splice(favoriteIndex, 1);
      }
    });

    return { parentMenuLinks, favoriteCMs };
  }

  private applyFavoriteChange(menu: ChildMenu, name: string, isFavorite: boolean): void {
    if (menu.name === name) {
      menu.isFavorite = isFavorite;
    }
    menu.childMenu?.forEach(child => this.applyFavoriteChange(child, name, isFavorite));
  }

  private applyEasyAccessFavorite(
    menu: ChildMenu,
    easyAccessFavorites: EasyAccessLink[],
    catalogInApplicability: Record<string, boolean>
  ): void {
    menu.disabled = menu.disabled || this.disableCatalogBasedOnApplicability(menu, catalogInApplicability);

    const matchingFavorites = easyAccessFavorites.filter(favorite => favorite.name === menu.name);
    if (matchingFavorites.length > 0) {
      menu.isFavorite = true;
      matchingFavorites.forEach(favorite => {
        favorite.label = menu.label;
        favorite.catalogId = menu.catalogId;
        favorite.disabled = menu.disabled;
        favorite.visible = menu.visible;
      });
    }

    menu.childMenu?.forEach(child =>
      this.applyEasyAccessFavorite(child, easyAccessFavorites, catalogInApplicability)
    );
  }

  // Normalize parentMenu entries so components with `componentType: 'menulinks'`
  // (which contain a `data` array) are expanded into plain ChildMenu objects
  // that the rest of the service expects.
  private normalizeParentMenu(parentMenuLinks: ParentMenu[]): ParentMenu[] {
    return (parentMenuLinks || []).map((pm) => {
      const normalized: ParentMenu = { ...pm, childMenu: [] } as ParentMenu;
      const children: any[] = (pm as any).childMenu || [];
      children.forEach((child) => {
        if (child?.componentType === 'menulinks' && Array.isArray(child?.data)) {
          child.data.forEach((d: any) => {
            normalized.childMenu.push(new ChildMenu(d));
          });
        } else {
          // already a ChildMenu-like object
          normalized.childMenu.push(new ChildMenu(child));
        }
      });
      const ordersIndex = normalized.childMenu.findIndex(child =>
        child.link?.replace(/^\//, '') === SRP_ROUTES.MYORDERSVIEW.PATH);
      const hasReturnOrders = normalized.childMenu.some(child =>
        child.link?.replace(/^\//, '') === SRP_ROUTES.MYRETURNORDERS.PATH
        || child.label === SRP_ROUTES.MYRETURNORDERS.NAME);
      if (ordersIndex !== -1 && !hasReturnOrders) {
        normalized.childMenu.splice(ordersIndex + 1, 0, new ChildMenu({
          name: 'my-return-orders',
          label: SRP_ROUTES.MYRETURNORDERS.NAME,
          link: `/${SRP_ROUTES.MYRETURNORDERS.PATH}`,
          category: 'internal',
          linkType: 'default',
          authField: 'none',
          linkDescription: '',
          parentMenuName: pm.label,
        }));
      }
      return normalized;
    });
  }



  private getEasyAccessFavourites(): Observable<ApiResponse<EasyAccessLink[]>> {
    if (!this.easyAccessFavouritesInFlight$) {
      this.easyAccessFavouritesInFlight$ = this.requestEasyAccessFavourites().pipe(
        finalize(() => (this.easyAccessFavouritesInFlight$ = null)),
        shareReplay({
          bufferSize: 1,
          refCount: false
        })
      );
    }
    return this.easyAccessFavouritesInFlight$;
  }

  private requestEasyAccessFavourites(): Observable<ApiResponse<EasyAccessLink[]>> {
    if (!environment.STUBS) {
      return mockEasyAccessResponse();
    } else {
      const payload = {
        userDetails: {
          userName: this.userDetails.userName,
          userId: this.userDetails.userId,
          currentContext: {
            roleName: this.userDetails.roleName,
          },
        },
      };
      return from(
        this.baseService.post<ApiResponse<EasyAccessLink[]>>(
          API_PATHS.PORTAL.GET_EASY_ACCESS,
          payload
        )
      )
    }
  }

  refreshCatalogInApplicability(parentMenu: ParentMenu[], favorites: ChildMenu[]) {
    const catalogInApplicability = this.catalogService.getCatalogInapplicability();
    parentMenu.forEach(parent => {
      parent.childMenu.forEach(child => {
        this.disableCatalogBasedOnApplicability(child, catalogInApplicability);
      });
    });
    favorites.forEach(fav => {
      this.disableCatalogBasedOnApplicability(fav, catalogInApplicability);
    });
    return { updatedParentMenu: [...parentMenu], updatedFavorites: [...favorites] };
  }

  private disableCatalogBasedOnApplicability(cm: ChildMenu, catalogInApplicability: Record<string, boolean>): boolean {
    if (cm.catalogId) {
      const status = this.catalogService.getVisibleAndDisabledStatusOfCatalog(cm.catalogId, catalogInApplicability);
      if (status === 'hide') {
        cm.disabled = true; // Hide the menu item by disabling it
        cm.visible = false;
      } else if (status === 'disabled') {
        cm.disabled = true; // Disable the menu item
        cm.visible = true;
      } else {
        cm.disabled = false; // Enable the menu item
        cm.visible = true;
      }
    }
    else {
      cm.disabled = false; // Enable the menu item
      cm.visible = true;
    }
    cm.childMenu?.forEach(child => this.disableCatalogBasedOnApplicability(child, catalogInApplicability));
    return cm.disabled;
  }

  addOrRemoveFavorite(link: ChildMenu, parentMenuName: string, remove = false): Observable<boolean> {
    this.favoriteChanges.set(link.name, remove ? null : { ...link, parentMenuName, isFavorite: true });
    if (!environment.STUBS) {
      this.favsChanged.next({ ...link, parentMenuName, isFavorite: !remove });
      return of(true);
    }
    const request = this.getAddOrRemoveFavRequest(link, parentMenuName);
    return from(this.baseService.post(remove ? API_PATHS.PORTAL.REMOVE_FAVOURITES : API_PATHS.PORTAL.ADD_FAVOURITES, request)).pipe(
      tap(() => this.favsChanged.next({ ...link, parentMenuName, isFavorite: !remove })),
      map(() => true),
      catchError((err) => {
        console.error(`Error ${remove ? 'removing' : 'adding'} favorite:`, err);
        return of(false); // emit void to complete the stream
      })
    );
  }

  private getAddOrRemoveFavRequest(
    link: ChildMenu,
    parentMenuName: string
  ): AddOrRemoveRequest {
    const request: AddOrRemoveRequest = {
      isFrequentlyVisited: !!link.isFrequentlyVisited,
      authField: link.authField || "",
      parentMenuName: parentMenuName || "",
      name: link.name || "",
      label: link.label || "",
      link: link.link || "",
      linkType: link.linkType || "",
      category: link.category || "",
      linkDescription: link.linkDescription || "",
      userDetails: {
        userName: this.userDetails.userName,
        currentContext: { roleName: this.userDetails.roleName },
      },
    };
    return request;
  }

  redirect(menu: ChildMenu, appContext?: string, parentLabel?: string) {
    const config = CATALOG_AUTH_CONFIG.find(c => c.appId === menu.label);

    if (!config) {
      if (menu.category.toLocaleLowerCase() === 'external') {
        window.open(menu.link, '_blank'); // '_blank' opens in a new tab
      } else {
        this.router.navigate([`/${menu.link}`]);
      }
    }

    this.buildUrlWithParams(config, appContext, parentLabel);
  }

  private buildUrlWithParams(config: CatalogAuthConfig, appContext?: string, parentLabel?: string) {

    if (config.category.toLocaleLowerCase() === 'external') {
      window.open(config.path, '_blank'); // '_blank' opens in a new tab
    } else if (parentLabel === 'Catalogs') {
      if (config.authType === AuthType.SAML) {
        this.reusableService.navigateToLink(null, window.location.origin, "_self", config.path, this.router);
      } else if (config.authType === AuthType.JWT) {
        this.gigyaService2.getJWT().then((t: string) => {
          this.reusableService.navigateToLink(null, window.location.origin, "_self", config.path + '?jwt=' + t, this.router);
        });
      } else {
        this.router.navigate([`/${SRP_ROUTES.ERROR_PAGE.PATH}`], {
          queryParams: {
            valid: true,
          }
        });
      }
    } else if (this.elementService.isEmbeddedInAngularApp && appContext && appContext === 'SRP') {
      //Use the service file
      this.router.navigate([config.path]);
    } else {
      window.location.href = config.path;
    }
  }
}